import { useEffect, useMemo, useState, type FormEvent } from 'react';
import type {
  CreateProjectPhaseInput,
  ProjectPhase,
  ProjectPhaseStatus,
} from '@catalyst-psa/shared';
import { PROJECT_PHASE_STATUSES } from '@catalyst-psa/shared';

import { ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/authContext.js';
import { Button } from '../../components/ui/Button.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { SelectField, TextField } from '../../components/ui/Field.js';
import { FormActions, useSaveMode } from '../../components/ui/FormActions.js';
import { Modal } from '../../components/ui/Modal.js';
import { useBillingClassOptions } from '../billing-classes/api.js';
import { useServiceCategoryOptions } from '../service-categories/api.js';
import {
  useCreateProjectPhase,
  useDeleteProjectPhase,
  useProject,
  useProjectRates,
  useUpdateProjectPhase,
} from './api.js';

interface PhaseFormModalProps {
  isOpen: boolean;
  projectId: string;
  /** Existing phase to edit, or null to create a new one. */
  phase: ProjectPhase | null;
  /** Suggested sequence for a new phase. */
  nextSequence: number;
  onClose: () => void;
}

interface FormState {
  name: string;
  sequence: string;
  status: ProjectPhaseStatus;
  /** Phase service category override ('' = use the project default). */
  overrideServiceCategoryId: string;
  /** Phase billing class override ('' = use the project default). */
  overrideBillingClassId: string;
  /** Phase override rate ('' = bill at the project header rate). */
  overrideRate: string;
}

const PHASE_STATUS_LABELS: Record<ProjectPhaseStatus, string> = {
  active: 'Active',
  completed: 'Completed',
};

const STATUS_OPTIONS = PROJECT_PHASE_STATUSES.map((status) => ({
  value: status,
  label: PHASE_STATUS_LABELS[status],
}));

function fieldErrorsOf(error: unknown): Record<string, string> {
  if (error instanceof ApiError && error.envelope?.error.details) {
    const result: Record<string, string> = {};
    for (const detail of error.envelope.error.details) {
      result[detail.path] = detail.message;
    }
    return result;
  }
  return {};
}

export function PhaseFormModal({
  isOpen,
  projectId,
  phase: phaseProp,
  nextSequence,
  onClose,
}: PhaseFormModalProps): JSX.Element {
  const [phase, setPhase] = useState<ProjectPhase | null>(phaseProp);
  const isEdit = phase !== null;
  const { isAdmin } = useAuth();
  const saveMode = useSaveMode();
  const [form, setForm] = useState<FormState>({
    name: '',
    sequence: '1',
    status: 'active',
    overrideServiceCategoryId: '',
    overrideBillingClassId: '',
    overrideRate: '',
  });
  const [confirmOpen, setConfirmOpen] = useState(false);

  const createMutation = useCreateProjectPhase(projectId);
  const updateMutation = useUpdateProjectPhase(projectId);
  const deleteMutation = useDeleteProjectPhase(projectId);
  const mutation = isEdit ? updateMutation : createMutation;

  const resetCreate = createMutation.reset;
  const resetUpdate = updateMutation.reset;
  const resetSaveMode = saveMode.reset;

  const projectQuery = useProject(projectId);
  const serviceCategoriesQuery = useServiceCategoryOptions();
  const billingClassesQuery = useBillingClassOptions();
  const projectRatesQuery = useProjectRates(projectId);

  /**
   * Project default service category, shown as the "use project default" baseline for the
   * phase category override.
   */
  const defaultCategory = useMemo(() => {
    const defaultId = projectQuery.data?.defaultServiceCategoryId;
    const all = serviceCategoriesQuery.data ?? [];
    return defaultId ? (all.find((category) => category.id === defaultId) ?? null) : null;
  }, [serviceCategoriesQuery.data, projectQuery.data]);

  /** Project default billing class, shown as the "use project default" baseline. */
  const defaultBillingClass = useMemo(() => {
    const defaultId = projectQuery.data?.defaultBillingClassId;
    const all = billingClassesQuery.data ?? [];
    return defaultId ? (all.find((cls) => cls.id === defaultId) ?? null) : null;
  }, [billingClassesQuery.data, projectQuery.data]);

  const categoryOptions = useMemo(() => {
    const all = serviceCategoriesQuery.data ?? [];
    return [
      {
        value: '',
        label: defaultCategory
          ? `Use project default (${defaultCategory.name})`
          : 'Use project default',
      },
      ...all.map((category) => ({ value: category.id, label: category.name })),
    ];
  }, [serviceCategoriesQuery.data, defaultCategory]);

  const billingClassOptions = useMemo(() => {
    const all = billingClassesQuery.data ?? [];
    return [
      {
        value: '',
        label: defaultBillingClass
          ? `Use project default (${defaultBillingClass.name})`
          : 'Use project default',
      },
      ...all.map((cls) => ({ value: cls.id, label: cls.name })),
    ];
  }, [billingClassesQuery.data, defaultBillingClass]);

  /** Effective project header rate for the default category: project rate if set, else catalog default. */
  const headerRate = useMemo((): { rate: string; source: 'project' | 'default' } | null => {
    const overrideCategoryId = form.overrideServiceCategoryId || defaultCategory?.id || null;
    if (!overrideCategoryId) return null;
    const projectRate = (projectRatesQuery.data?.data ?? []).find(
      (rate) => rate.serviceCategoryId === overrideCategoryId,
    );
    if (projectRate) return { rate: projectRate.rate, source: 'project' };
    const category = (serviceCategoriesQuery.data ?? []).find(
      (item) => item.id === overrideCategoryId,
    );
    return category ? { rate: category.defaultRate, source: 'default' } : null;
  }, [
    form.overrideServiceCategoryId,
    defaultCategory,
    projectRatesQuery.data,
    serviceCategoriesQuery.data,
  ]);

  useEffect(() => {
    if (isOpen) {
      setPhase(phaseProp);
    }
  }, [isOpen, phaseProp]);

  useEffect(() => {
    if (isOpen) {
      setForm(
        phase
          ? {
              name: phase.name,
              sequence: String(phase.sequence),
              status: phase.status,
              overrideServiceCategoryId: phase.overrideServiceCategoryId ?? '',
              overrideBillingClassId: phase.overrideBillingClassId ?? '',
              overrideRate: phase.overrideRate ?? '',
            }
          : {
              name: '',
              sequence: String(nextSequence),
              status: 'active',
              overrideServiceCategoryId: '',
              overrideBillingClassId: '',
              overrideRate: '',
            },
      );
      setConfirmOpen(false);
      resetSaveMode();
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, phase, nextSequence, resetSaveMode, resetCreate, resetUpdate]);

  const fieldErrors = fieldErrorsOf(mutation.error);
  const generalError =
    mutation.error instanceof ApiError && !mutation.error.envelope?.error.details?.length
      ? mutation.error.message
      : mutation.isError && !(mutation.error instanceof ApiError)
        ? 'Failed to save phase.'
        : null;

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const sequence = Number.parseInt(form.sequence, 10);
    const trimmedOverride = form.overrideRate.trim();
    const payload: CreateProjectPhaseInput = {
      name: form.name.trim(),
      sequence: Number.isFinite(sequence) ? sequence : undefined,
      status: form.status,
      overrideServiceCategoryId:
        form.overrideServiceCategoryId === '' ? null : form.overrideServiceCategoryId,
      overrideBillingClassId:
        form.overrideBillingClassId === '' ? null : form.overrideBillingClassId,
      overrideRate: trimmedOverride === '' ? null : trimmedOverride,
    };

    if (isEdit) {
      updateMutation.mutate(
        { id: phase.id, input: payload },
        { onSuccess: () => saveMode.closeAfterSaveRef.current && onClose() },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: (created) => {
          if (saveMode.closeAfterSaveRef.current) {
            onClose();
          } else {
            setPhase(created);
          }
        },
      });
    }
  };

  const handleDelete = (): void => {
    if (!phase) {
      return;
    }
    deleteMutation.mutate(phase.id, {
      onSuccess: () => {
        setConfirmOpen(false);
        onClose();
      },
    });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? 'Edit phase' : 'New phase'}
        footer={
          <FormActions
            formId="phase-form"
            saveMode={saveMode}
            isPending={mutation.isPending}
            onCancel={onClose}
            leading={
              isEdit && isAdmin ? (
                <Button
                  variant="ghost"
                  className="mr-auto text-rose-600 hover:bg-rose-50"
                  onClick={() => setConfirmOpen(true)}
                  disabled={mutation.isPending || deleteMutation.isPending}
                >
                  Delete
                </Button>
              ) : null
            }
          />
        }
      >
        <form id="phase-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
          {generalError ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {generalError}
            </div>
          ) : null}

          <TextField
            label="Name"
            required
            value={form.name}
            maxLength={200}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            error={fieldErrors.name}
          />

          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="Sequence"
              type="number"
              min={1}
              value={form.sequence}
              onChange={(e) => setForm((f) => ({ ...f, sequence: e.target.value }))}
              error={fieldErrors.sequence}
            />
            <SelectField
              label="Status"
              value={form.status}
              options={STATUS_OPTIONS}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value as ProjectPhaseStatus }))
              }
              error={fieldErrors.status}
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-slate-700">Billing</span>
            <p className="text-xs text-slate-500">
              Tickets and time in this phase default to the project&apos;s service category and
              billing class. Override them here to bill this phase differently. Leave the rate blank
              to bill at the project header rate.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <SelectField
                label="Service category"
                value={form.overrideServiceCategoryId}
                options={categoryOptions}
                disabled={serviceCategoriesQuery.isLoading || projectQuery.isLoading}
                onChange={(e) =>
                  setForm((f) => ({ ...f, overrideServiceCategoryId: e.target.value }))
                }
                error={fieldErrors.overrideServiceCategoryId}
              />
              <SelectField
                label="Billing class"
                value={form.overrideBillingClassId}
                options={billingClassOptions}
                disabled={billingClassesQuery.isLoading || projectQuery.isLoading}
                onChange={(e) => setForm((f) => ({ ...f, overrideBillingClassId: e.target.value }))}
                error={fieldErrors.overrideBillingClassId}
              />
              <TextField
                label="Override rate"
                type="number"
                min={0}
                step="0.0001"
                value={form.overrideRate}
                placeholder={headerRate?.rate ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, overrideRate: e.target.value }))}
                error={fieldErrors.overrideRate}
              />
            </div>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        isOpen={confirmOpen}
        title="Delete phase"
        message={`Delete "${phase?.name ?? ''}"? Its tickets and time entries will be detached from the phase.`}
        confirmLabel="Delete"
        destructive
        isConfirming={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}
